// widget/src/components/MessageCard.tsx

import type React from "react"
import type { ColorCardData, ProductCardData, Message } from "../types"
import { cn } from "../util"
import styles from "../styles/widget.module.css"
import { ProductCard } from "./ProductCard"
import { ColorCard } from "./ColorCard"
import { TrackingCard } from "./TrackingCard"
import { DiscountChip } from "./DiscountChip"
import { EmailCaptureForm } from "./EmailCaptureForm"
import { VideoCard } from "./VideoCard"
import { Chips } from "./Chips"
import { BuildStepCard } from "./BuildStepCard"
import { BuildSummaryCard } from "./BuildSummaryCard"

/** Strip backend-internal markers and lightweight markdown so the bubble
 *  shows clean prose even mid-stream. The cleanup tags `<<CHIPS>>...</CHIPS>>`
 *  and `<<INTENT>>...</INTENT>>` are emitted alongside the visible text and
 *  only get removed by the backend at end-of-stream via the `text_clean`
 *  event — until that arrives the markup is visible to the customer. We
 *  also strip markdown bold/italic/headers/bullets because the model still
 *  sneaks them in despite the system prompt; rendering them as plain prose
 *  is more chat-appropriate than installing a markdown renderer. */
export function cleanForDisplay(text: string): string {
  return (
    text
      // Hide CHIPS/INTENT marker blocks even mid-stream (open without close).
      .replace(/<<CHIPS>>[\s\S]*?(?:<<\/CHIPS>>|$)/g, "")
      .replace(/<<INTENT>>[\s\S]*?(?:<<\/INTENT>>|$)/g, "")
      // Markdown links [text](url) — drop the URL, keep the text. Pearl
      // is told the cards render automatically, but if she slips and
      // pastes a link, showing "[name](url)" raw is worse than just the
      // name. The card attachment already provides the click-through.
      .replace(/\[([^\]\n]+)\]\(([^)\n]+)\)/g, "$1")
      // Markdown bold/italic — keep the inner text, drop the asterisks.
      .replace(/\*\*([^*\n]+)\*\*/g, "$1")
      .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1")
      // Heading hashes and bullet hyphens at line start.
      .replace(/^[ \t]*#{1,6}\s+/gm, "")
      .replace(/^[ \t]*[-*]\s+/gm, "")
      // Inline backticks — keep the text, drop the ticks.
      .replace(/`([^`\n]+)`/g, "$1")
      .trimEnd()
  )
}

interface Props {
  message: Message
  /** Called when the user clicks a chip — submits the chip's text as a new message. */
  onSubmitText?: (text: string) => void
  /** Called when the user submits the email-capture form. */
  onSubmitEmail?: (email: string) => void
}

export function MessageCard({ message, onSubmitText, onSubmitEmail }: Props) {
  if (message.role === "user") {
    return (
      <div className={cn(styles.msg, styles.user)}>
        <div className={styles.card}>
          <div className={styles.text}>{message.text}</div>
        </div>
        <div
          className={cn(styles.avatar, styles["avatar-user"])}
          aria-hidden="true"
        />
      </div>
    )
  }

  const pendingTools = (message.tools ?? []).filter((t) => t.status === "pending")
  const attachments = message.attachments ?? []
  // Suppress inline suggestion chips when a build-card attachment is
  // present — the BuildStepCard / BuildSummaryCard already render their
  // own chips inside the canvas, so emitting message.chips inline below
  // the bubble produced a confusing dual chip strip. Render-only
  // suppression; state still carries the chips.
  const hasBuildAttachment = attachments.some(
    (a) => a.kind === "build_step" || a.kind === "build_summary",
  )
  const chips = hasBuildAttachment ? [] : message.chips ?? []
  const displayText = cleanForDisplay(message.text)

  // Suppress empty assistant bubbles in BOTH states:
  //   - pending + no content yet: the global composing indicator is the
  //     loading visual; rendering an empty card next to it produced a
  //     confusing "two bubbles" effect.
  //   - done + no content: the Pearl-off path emits a `done` SSE event
  //     with no tokens for the "silent ack" case (after the first
  //     handoff greeting fires, subsequent customer messages are saved
  //     to DB silently — no visible reply). Without this guard, the
  //     silent ack renders as a stray empty "Pearl JUST NOW" bubble.
  const noContent =
    !displayText && pendingTools.length === 0 && attachments.length === 0
  if (noContent) {
    return chips.length > 0 ? (
      <Chips chips={chips} onPick={(c) => onSubmitText?.(c)} />
    ) : null
  }

  return (
    <>
      <div className={cn(styles.msg, styles.assistant)}>
        <div
          className={cn(styles.avatar, styles["avatar-pearl"])}
          aria-hidden="true"
        />
        <div className={styles.card}>
          <div className={styles.meta}>
            {/*
              When senderName is present, this row is a real human teammate's
              reply (chat_messages.role='agent' in the DB) — show their name and
              drop the "Beta" tag so the customer sees "Justin from DipYourCar
              JUST NOW" rather than the AI persona. Otherwise this is Pearl.
            */}
            <span className={styles["meta-name"]}>
              {message.senderName || "Pearl"}
            </span>
            {!message.senderName && (
              <span className={styles["beta-tag"]}>Beta</span>
            )}
            <span className={styles["meta-time"]}>JUST NOW</span>
          </div>
          <div className={styles.text}>
            {displayText}
            {message.pending && displayText && (
              <span className={styles.caret}>▍</span>
            )}
          </div>

          {pendingTools.map((t) => (
            <div key={t.id} className={styles["tool-line"]}>
              {humanizeTool(t.name)}…
            </div>
          ))}

          {attachments.length > 0 && (
            <div className={styles.attachments}>
              {renderAttachments(attachments, onSubmitEmail, onSubmitText)}
            </div>
          )}
        </div>
      </div>

      {chips.length > 0 && (
        <Chips chips={chips} onPick={(c) => onSubmitText?.(c)} />
      )}
    </>
  )
}

/** Render attachments in order, but pack consecutive `color` attachments
 *  into a single 2-column grid wrapper. find_color always returns 2-5
 *  colors at once via the same action event, so in practice this
 *  collapses every color batch into one grid. Other attachment kinds
 *  render full-width as before. */
function renderAttachments(
  attachments: NonNullable<Message["attachments"]>,
  onSubmitEmail: ((email: string) => void) | undefined,
  onUserReply: ((text: string) => void) | undefined,
): React.ReactNode[] {
  const out: React.ReactNode[] = []
  let colorBuffer: { kind: "color"; data: ColorCardData }[] = []
  let productBuffer: { kind: "product"; data: ProductCardData }[] = []

  // Colors and products both pack into a 2-column grid when 2+ come back
  // at once. A single item renders full-width as a normal card.
  function flushColors(key: string) {
    if (colorBuffer.length === 0) return
    if (colorBuffer.length === 1) {
      out.push(<ColorCard key={key} data={colorBuffer[0].data} />)
    } else {
      out.push(
        <div key={key} className={styles["color-grid"]}>
          {colorBuffer.map((c, i) => (
            <ColorCard key={i} data={c.data} />
          ))}
        </div>,
      )
    }
    colorBuffer = []
  }

  function flushProducts(key: string) {
    if (productBuffer.length === 0) return
    if (productBuffer.length === 1) {
      out.push(<ProductCard key={key} data={productBuffer[0].data} />)
    } else {
      out.push(
        <div key={key} className={styles["product-grid"]}>
          {productBuffer.map((p, i) => (
            <ProductCard key={i} data={p.data} />
          ))}
        </div>,
      )
    }
    productBuffer = []
  }

  function flushAll(key: string) {
    flushColors(`${key}-c`)
    flushProducts(`${key}-p`)
  }

  attachments.forEach((att, idx) => {
    if (att.kind === "color") {
      flushProducts(`pg-${idx}`)
      colorBuffer.push(att)
      return
    }
    if (att.kind === "product") {
      flushColors(`cg-${idx}`)
      productBuffer.push(att)
      return
    }
    flushAll(`fa-${idx}`)
    switch (att.kind) {
      case "tracking":
        out.push(<TrackingCard key={idx} data={att.data} />)
        break
      case "discount":
        out.push(<DiscountChip key={idx} data={att.data} />)
        break
      case "video":
        out.push(<VideoCard key={idx} data={att.data} />)
        break
      case "email_capture":
        out.push(
          <EmailCaptureForm
            key={idx}
            onSubmit={(email) => onSubmitEmail?.(email)}
          />,
        )
        break
      case "build_step":
        out.push(
          <BuildStepCard
            key={idx}
            data={att.data}
            onChipClick={(label) => onUserReply?.(label)}
          />,
        )
        break
      case "build_summary":
        out.push(
          <BuildSummaryCard
            key={idx}
            data={att.data}
            onAddToCart={(_variantId, title) =>
              onUserReply?.(`Add ${title} to cart`)
            }
            onUserReply={(text) => onUserReply?.(text)}
          />,
        )
        break
    }
  })
  flushAll("end")
  return out
}

function humanizeTool(name: string): string {
  switch (name) {
    case "track_order":
      return "Looking up your order"
    case "find_color":
      return "Finding that color"
    case "search_products":
      return "Checking the catalog"
    case "show_product_card":
      return "Pulling up the product"
    case "search_knowledge":
      return "Checking our guides"
    case "get_size_guide":
      return "Checking sizing"
    case "decode_vehicle":
      return "Looking up your vehicle"
    case "navigate_product":
      return "Opening the page"
    case "add_to_cart":
      return "Adding to your cart"
    case "offer_discount":
      return "Checking discounts"
    case "capture_email":
      return "Noting your email"
    case "handoff_to_agent":
      return "Grabbing a teammate"
    case "get_customer_context":
      return "Checking your history"
    default:
      return "Checking"
  }
}
